"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isAutoBucketingHistoryObject = exports.AutoBucketHistory = void 0;
const tslib_1 = require("tslib");
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const lambda_1 = require("../../../util/lambda");
const number_1 = require("../../../util/number");
const TimeBuckets_1 = require("../../../util/time/Periodization/TimeBuckets");
const millisecondOperations_1 = require("../../../util/time/millisecondOperations");
const HistoryStore_1 = require("../HistoryStore");
const enumerableDecorator_1 = require("../enumerableDecorator");
const types_1 = require("./types");
const types_2 = require("../../../util/time/Periodization/types");
const util_1 = require("../../../util");
const debug = lambda_1.DEBUG_OVERRIDE || (lambda_1.NOT_LAMBDA && false);
/** gets the fromTimestamp for the inferred period as if the provided timestamp is the toTimestamp,
 * the inference is: get the fromTimestamp based on time before the beginning of the bucket that would give the same period as the bucket duration.
 * If the bucket was 1 hour, and the reading was at 20 mins, the inferred timestamp would be 40 mins before the start of the bucket
 */
const inferLastReadingTimestamp = ({ timestamp, timeBucketDef }) => {
    const thisBucketStart = (0, util_1.getTimeBucketStartDateTimeForTimestamp)({ timeBucketDef, timestamp }).valueOf();
    const startOffset = timestamp - thisBucketStart;
    const bucketLength = timeBucketDef.unit * timeBucketDef.width;
    return thisBucketStart - (bucketLength - startOffset);
};
const defaultTimeBucketDef = { unit: types_2.TimeBucketUnits.MINUTE, width: 10, offset: 0 };
/** Automatically buckets provided readings into buckets of defined size
 * Basic structure is an array of timestamps where timestamp[N] and timestamp[N+1] are one period apart
 * A bucket is an array of equal length to timestamp where key[N] is the value between timestamp[N] and one period in the future
 * E.g. If time buckets are 1 hour apart, bucket labelled 11:00 contains readings from 11:00:00 (inclusive) to 12:00:00 (exclusive)
 */
class AutoBucketHistory extends HistoryStore_1.HistoryStore {
    constructor(zombie) {
        const { timeBucketDef, defaultBucketStrategy, bucketStrategyMap, ...others } = zombie ?? {};
        super(others);
        // Updates whenever a reading is written to, even in the past. More for record keeping
        this.lastReadingTimestamps = {};
        /** Keeps track of most recent time each key has been pushed */
        this.latestNonNullReadingTimestamp = {};
        /** Currently most attributes types are readings not counts, despite requirement docs which are based on horse monitor */
        this.getKeyStrategy = (k) => this.bucketStrategyMap[k] ?? this.defaultBucketStrategy;
        this.setKeyStrategy = (k, bucketStrategy) => {
            this.bucketStrategyMap[k] = bucketStrategy;
        };
        this.loadKeyStrategyMap = (keyStrategyMap) => {
            Object.entries(keyStrategyMap).forEach(([key, strategy]) => {
                this.setKeyStrategy(key, strategy);
            });
        };
        this.niceDates = () => {
            return this.timestamp.map((t) => new Date(t));
        };
        /** inserts and fills array if new, does not update value */
        this.updateWithKeyAndBucket = ({ k, timestamp }) => {
            let bucketStartIdx = this.timestamp.findIndex((t) => t === timestamp);
            if (bucketStartIdx === -1) {
                bucketStartIdx = this.insertBucket(timestamp, k);
            }
            if (!this[k]) {
                this[k] = new Array(this.timestamp.length).fill(null);
            } /** not same as back fill as populates complete length */
            return bucketStartIdx;
        }; /*( { k, timestamp }: { k: string, timestamp: number }) => this.insertBucket(timestamp, k)/**/
        // For math reasons, the number in UTC+13 needs to be negated to line up properly.
        if (zombie?.timeBucketDef.UTCOffset) {
            zombie.timeBucketDef.offset = -zombie.timeBucketDef.UTCOffset;
        }
        // Correct for offsets that are larger than the period size of the bucket
        // These are still fine, but the offset needs to be reduced by the number of whole periods that fit inside
        if (zombie?.timeBucketDef.offset)
            zombie.timeBucketDef.offset = zombie.timeBucketDef.offset % (zombie.timeBucketDef.unit * zombie.timeBucketDef.width);
        this.timeBucketDef = { ...defaultTimeBucketDef, ...zombie?.timeBucketDef };
        this.protectedKeys = [...this.protectedKeys, 'timeBucketDef', 'lastReadingTimestamps', 'bucketStrategyMap'].reduce((a, c) => {
            if (!a.includes(c)) {
                a.push(c);
            }
            return a;
        }, []);
        this.defaultBucketStrategy = defaultBucketStrategy ?? types_1.BucketStrategy.INFERREADING;
        this.bucketStrategyMap = bucketStrategyMap ?? {};
        this.timeOfLastPush = {};
        /**
         * todo lastReadingTimestamps bucketStrategyMap
         */
    }
    /** adds array if necessary, then if bucket does not exist either unshifts, splices or pushes bucket into all keys,
     * @param timestamp - ??
     * @param key - The key for the new bucket (probably equal to the reading name)
     */
    insertBucket(timestamp, key) {
        // If the key has never been used, we need to create the new bucket
        if (!this[key]) {
            if (this.getLength() === 0) {
                debug && console.log(`Adding new ${key}`);
                this[key] = [];
            }
            else {
                debug && console.log(` backfilling ${key} with ${this.getLength() - 1} nulls`);
                /** backfill assumes a push will be made */
                this.backFill(key);
            }
        }
        let bucketInsertIdx = this.timestamp.findIndex((t) => t >= timestamp);
        const bucketExists = this.timestamp[bucketInsertIdx] === timestamp;
        if (bucketExists)
            return bucketInsertIdx;
        if (bucketInsertIdx === -1) {
            // Add Bucket at end
            if (timestamp > (this.getFirstTimestamp() ?? 0)) {
                bucketInsertIdx = this.timestamp.length;
                this.timestamp.push(timestamp);
                this.keys().forEach((key) => {
                    // debug && console.log(`push ${ null } to ${ key } with ${ bucketInsertIdx }`, this.timestamp)
                    this[key].push(null);
                });
            }
            else {
                bucketInsertIdx = 0;
                this.timestamp.unshift(timestamp);
                this.keys().forEach((key) => {
                    debug && console.log(`unshifting ${null} to ${key}`);
                    this[key].unshift(null);
                });
            }
        }
        else {
            this.timestamp.splice(bucketInsertIdx, 0, timestamp);
            this.keys().forEach((key) => {
                debug && console.log(`splicing ${null} to ${key} of lenght ${this[key].length} at ${bucketInsertIdx}`);
                this[key].splice(bucketInsertIdx, 0, null);
                if (this[key].length < this.getLength()) {
                    /** this is an side effect of backfill assuming a push after it is called */
                    debug && console.log(`Spliced ${key} has less length, adding nulls`);
                    while (this[key].length < this.getLength()) {
                        this[key].push(null);
                    }
                }
            });
        }
        return bucketInsertIdx;
    }
    getLength() {
        debug && console.log('getLenght in sub class');
        return this.timestamp.length;
    }
    getLastTimestamp() {
        return this.timestamp[this.timestamp.length - 1];
    }
    addToBuckets(apportion, k, v) {
        const [fromTimestamp, toTimestamp] = apportion.bucket;
        let bucketStartIdx = this.updateWithKeyAndBucket({ k, timestamp: fromTimestamp });
        const targetBucket = this[k]?.[bucketStartIdx];
        debug && console.log({ bucketStartIdx, thisK: this[k] });
        if ((0, number_1.isNumber)(v)) {
            if (targetBucket === null || (0, number_1.isNumber)(targetBucket)) {
                this[k][bucketStartIdx] = (targetBucket ?? 0) + v * apportion.apportionment;
            }
            else {
                debug && console.log(`invalid bucket contents for key ${k} with bucketStartIndex ${bucketStartIdx}`);
                try {
                    if (targetBucket === undefined) {
                        debug && console.log(`The target bucket value is undefined`);
                        debug && console.log(`Last existing index ${this[k].length - 1}`, this.getArrayLength());
                    }
                    else {
                        debug && console.log(`The target bucket value is neither null, number or undefined`, targetBucket);
                    }
                    throw new Error('VSCode breakpoint execption');
                }
                catch (e) {
                    debug && console.log(e.message);
                    if (debug) {
                        throw e;
                    }
                }
            }
        }
        else {
            console.log(`No value for ${k}, ${v}`);
        }
    }
    /** startTimestamp is last reading timestamp, end timestamp is timestamp of reading */
    inferReading({ timeBucketDef, startTimestamp, endTimestamp, k, v }) {
        const lastBucketTimestamp = (0, util_1.getTimeBucketStartDateTimeForTimestamp)({ timeBucketDef, timestamp: startTimestamp }).valueOf();
        const currentBucketTimestamp = (0, util_1.getTimeBucketStartDateTimeForTimestamp)({ timeBucketDef, timestamp: endTimestamp }).valueOf();
        //console.log("Bucket start for", getNiceDate(endTimestamp), ": ", getNiceDate(currentBucketTimestamp))
        const buckets = (0, TimeBuckets_1.getTimeBuckets)({ timeBucketDef, startTimestamp: lastBucketTimestamp, endTimestamp });
        //debug && console.log("Buckets needed for infer", buckets);
        const bucketsToUpdate = buckets.map((bucket) => {
            const bucketIdx = this.insertBucket(bucket[0], k);
            const value = this[k][bucketIdx];
            return { bucketIdx, bucket, value };
        });
        //console.log(this.timestamp.map(ts => getNiceDate(ts)))
        debug && console.log('bucketContents', bucketsToUpdate);
        /** this updates the latest bucket */
        const updateIdx = this.updateWithKeyAndBucket({ k, timestamp: currentBucketTimestamp });
        this[k][updateIdx] = v;
        //const firstBucketContents = bucketContents.shift()
        const lastBucketContents = bucketsToUpdate.pop(); // We've updated the last bucket, so remove it
        // We leave the first bucket in, but it will get set to itself so it's okay
        // This also handles the case where we only have 1 bucket
        const startValue = bucketsToUpdate[0]?.value ?? 0; // temp
        // If there is no start value, we treat the value as ramping up from 0 from prior value to current value
        // Maybe we want to assume the value at we want to use the last non-null value, not 0!
        //            _                __
        // ____   ,.-*|   vs  ____    / |
        //    |___|   |          |___/  |
        //   Proposed           Current
        const valDiff = (v ?? 0) - startValue;
        //const remaininBuckets = bucketContents
        bucketsToUpdate.forEach((b, i) => {
            const { bucketIdx, bucket, value } = b;
            const inferredValue = startValue + (i / bucketsToUpdate.length) * valDiff;
            this[k][bucketIdx] = inferredValue;
            b.value = inferredValue;
        });
        // const updateIdx = this.updateWithKeyAndBucket( { k, timestamp: currentBucketTimestamp })
        // this[k][ updateIdx ] = v
        //debug && console.log('Infer readings, buckets', { first: firstBucketContents?.value, last: lastBucketContents?.value, valDiff, remaininBuckets })
        /** insert any missing buckets.
         * get the current values in each bucket - which should only be in the first - but may be empty as added by other keys
         * infer values across buckets
         * update buckets.
         */
    }
    /** apportion measures in map to time buckets found usein the snapshot timestamp, and either a provided previous timestamp, the last processed timestamp
     * or if the first ever reading a pseudo fromTimestamp based on the duration of the bucket
     * The previousTimestamp is optional and can be used, for example to add snapshots out of order. In this case ALL pushes must provide a previous timestamp
     *
     * update is inherited and if set to true or undefined will update statistics
     * */
    push(snapshot, update, previousTimestamp) {
        const timeBucketDef = this.timeBucketDef;
        const { timestamp: readingTimestamp, state } = snapshot;
        /** to deal with state keys that are sparsely populated over reading, each key value is has its apportionment calculated over the period from the first and current non null value times */
        Object.entries(state).forEach(([k, v]) => {
            debug && console.log(`push for ${k}: ${v}`);
            if ((0, number_1.isNumber)(v)) {
                const lastReadingTimestamp = this.lastReadingTimestamps[k];
                /** the last non null value, or if the first value for k, the start of the bucket */
                //console.log("ABH: prev timestamp", previousTimestamp);
                const fromTimestamp = previousTimestamp ?? lastReadingTimestamp ?? inferLastReadingTimestamp({ timeBucketDef, timestamp: readingTimestamp });
                const toTimestamp = readingTimestamp;
                const bucketStrategy = this.getKeyStrategy(k);
                debug && console.log('Strategy', bucketStrategy);
                if (bucketStrategy === types_1.BucketStrategy.APPORTION) {
                    const apportions = (0, TimeBuckets_1.getApportionToTimeBucketsMap)({ timeBucketDef, measureRange: { fromTimestamp, toTimestamp } });
                    debug && console.log(`${k} from ${(0, millisecondOperations_1.getNiceDate)(fromTimestamp)}, `, apportions, { fromTimestamp, toTimestamp });
                    apportions.forEach((apportion) => {
                        debug && console.log(`addToBucket`, { k, v }, apportion);
                        this.addToBuckets(apportion, k, v);
                    });
                }
                else if (bucketStrategy === types_1.BucketStrategy.INFERREADING) {
                    const inferReadingInput = {
                        timeBucketDef: this.timeBucketDef,
                        k,
                        v,
                        startTimestamp: fromTimestamp,
                        endTimestamp: toTimestamp,
                    };
                    this.inferReading(inferReadingInput);
                }
                else if (bucketStrategy === types_1.BucketStrategy.MAXIMUM) {
                    throw new Error(`no bucket strategy for ${types_1.BucketStrategy.MAXIMUM}`);
                }
                else if (bucketStrategy === types_1.BucketStrategy.MINIMUM) {
                    throw new Error(`no bucket strategy for ${types_1.BucketStrategy.MINIMUM}`);
                }
                else {
                    throw new Error(`no bucket strategy for ${k}`);
                }
                // Keep record of time of **last** push to this key
                this.lastReadingTimestamps[k] = readingTimestamp;
                // Keep record of time of latest push to this key
                this.latestNonNullReadingTimestamp[k] = Math.max(this.latestNonNullReadingTimestamp[k] ?? -Infinity, readingTimestamp);
            }
        });
        return this;
    }
    ingest(orderedSnapshots) {
        // Needs to ingest based on key...
        orderedSnapshots.forEach((snapshot, idx) => {
            if (orderedSnapshots[idx - 1]) {
                this.push(snapshot, false, orderedSnapshots[idx - 1].timestamp);
            }
            else {
                this.push(snapshot);
            }
        });
    }
    /** Each entry of a given key in a history store represents the occurence of that key since the last non-null reading of that key.
      If different entries in the history store are updated at different intervals, you could have multiple messages between non-null entries of a given key.
      The other ingest method assumes the previous timestamp for all keys in the snapshot is the previous snapshot. With differing intervals, this is not necessarily true.
      The solution: keep track of the last non-null timestamp for each key individually, and use recorded timestamp as previous timestamp for that reading when bucketing
      * See also: pushKeywise
      * @param {StateSnapshot<any>[]} orderedSnapshots - a list of state snapshots ordered earliest - latest
      */
    ingestKeywise(orderedSnapshots) {
        let latestKeyTimestamp = {};
        // Reading: latest timestamp for non-null reading
        orderedSnapshots.forEach((snapshot, idx) => {
            Object.entries(snapshot.state).forEach(([readingKey, value]) => {
                if (value == null)
                    return;
                if (latestKeyTimestamp[readingKey]) {
                    debug && console.log(`Pushing ${readingKey} with value ${value}, last reading was at ${(0, millisecondOperations_1.getNiceDate)(latestKeyTimestamp[readingKey])}`);
                    this.push({ timestamp: snapshot.timestamp, state: { [readingKey]: value } }, true, latestKeyTimestamp[readingKey]);
                }
                else {
                    debug && console.log(`Pushing ${readingKey} with value ${value}, this is the first non-null reading`);
                    this.push({ timestamp: snapshot.timestamp, state: { [readingKey]: value } }, true);
                }
                latestKeyTimestamp[readingKey] = snapshot.timestamp;
            });
            debug && console.log(`ABH ingesting keywise state after push ${idx + 1}`, this);
        });
    }
    /** Bucket keys in provided state snapshot according to latest timestamp for that reading, rather than timestamp of last snapshot
     * @param {StateSnapshot<any>} snapshot - Snapshot to push keywise
     * @param {{[readingKey: string]: number | null}} [latestTimestampOverrides={}] - Way to provide latest timestamp of non null readings externally (if not provided the latest non null timestamp from the autobucket history will be used)
     */
    pushKeywise(snapshot, latestTimestampOverrides = {}) {
        debug && console.log('Retrieving Last Non Null from Inside ABH');
        let latestNonNullKeyTimestamps = this.getIndexLastNonNullKeyLessThanTimestamp(snapshot.timestamp, 'timestamps');
        /**
         * Because of how getIndexLastNonNullKeyLessThanTimestamp works, it can sometimes return the timestamp of the latest bucket, but this does *not* line up with the time at the latest push, depending on when it is.
         * E.g. we push at 13:00, it gets bucketed from 12:00-13:00, but the bucket it creates is labelled 12:00. Then we push at 13:30. The above method will return 12:00 as the latest non-null timestamp (because that's what the bucket says), but actually the timestamp of last push was 13:00.
         *
         * To counter this, AutoBucketHistory keeps track of the most recent non-null push for each key. It is used here if it is
         * 1. Later than the above-returned latestNonNullKeyTimestamp, and
         * 2. Before the timestamp of the provided snapshot
         */
        Object.entries(latestNonNullKeyTimestamps).forEach(([readingKey, latestNonNullTimestamp]) => {
            if (!this.latestNonNullReadingTimestamp[readingKey])
                return;
            if ((latestNonNullTimestamp ?? 0) < this.latestNonNullReadingTimestamp[readingKey] && this.latestNonNullReadingTimestamp[readingKey] < snapshot.timestamp)
                latestNonNullKeyTimestamps[readingKey] = this.latestNonNullReadingTimestamp[readingKey];
        });
        Object.entries(snapshot.state).forEach(([readingKey, value]) => {
            if (value == null)
                return;
            if (latestTimestampOverrides[readingKey]) {
                this.push({ timestamp: snapshot.timestamp, state: { [readingKey]: value } }, false, latestTimestampOverrides[readingKey]);
            }
            else if (latestNonNullKeyTimestamps[readingKey]) {
                console.log('Have latest non null', (0, millisecondOperations_1.getNiceDate)(latestNonNullKeyTimestamps[readingKey]));
                this.push({ timestamp: snapshot.timestamp, state: { [readingKey]: value } }, false, latestNonNullKeyTimestamps[readingKey]);
            }
            else {
                console.log('No info about latest timestamp');
                this.push({ timestamp: snapshot.timestamp, state: { [readingKey]: value } });
            }
        });
    }
    /** Hidden assumption of this method: all buckets have values representing the entire hour
     * If you want to apportion only the past (if, for example, you are trying to get the last X hours from the latest message), you can provide the latest timestamp here
     * */
    apportionedSliceByTimestamp(timestampRange, latestTimestamp = undefined) {
        debug &&
            console.log('ABH Seeking', timestampRange.map((ts) => new Date(ts)));
        debug &&
            console.log('ABH Dates in ABH', this.getStateSnapshotArray().map((s) => new Date(s.timestamp)));
        const bucketSize = this.timeBucketDef.width * this.timeBucketDef.unit;
        if (latestTimestamp) {
            if (latestTimestamp < timestampRange[0])
                throw RangeError(`Attempting to get range that exists in the future of a defined latest timestamp (${(0, millisecondOperations_1.getNiceDate)(latestTimestamp)}) represented by the bucket history`);
            if (timestampRange[1] > latestTimestamp)
                throw RangeError(`Attempting to get range that extends into the future of a defined latest timestamp (${(0, millisecondOperations_1.getNiceDate)(latestTimestamp)}) represented by the bucket history, sought range ${(0, millisecondOperations_1.getNiceDate)(timestampRange[0])} - ${(0, millisecondOperations_1.getNiceDate)(timestampRange[1])}`);
        }
        const bucketsOfInterest = this.sliceByTimestamp([timestampRange[0], timestampRange[1]]); //- bucketSize])
        debug && console.log('ABH Sliced', bucketsOfInterest);
        // If buckets exist outside of range only, get rid of them and return nothing
        if (timestampRange[1] < (bucketsOfInterest.getFirstTimestamp() ?? 0) || timestampRange[0] > (bucketsOfInterest.getLastTimestamp() ?? 0) + bucketSize) {
            while (bucketsOfInterest.getLength() > 0)
                bucketsOfInterest.pop();
            return new HistoryStore_1.HistoryStore();
        }
        if (bucketsOfInterest.getLength() == 1) {
            // Only one bucket!
            const totalTime = timestampRange[1] - timestampRange[0];
            debug && console.log('Returning single bucket with total time', totalTime);
            let thisBucketSize = bucketSize;
            if (latestTimestamp) {
                thisBucketSize = Math.min(latestTimestamp - (bucketsOfInterest.getFirstTimestamp() ?? 0), bucketSize);
            }
            const proportion = totalTime / thisBucketSize;
            bucketsOfInterest.keys().forEach((key) => {
                bucketsOfInterest[key][0] *= proportion;
            });
            return bucketsOfInterest;
        }
        //debug && console.log("ABH BoI Da: ", bucketsOfInterest.timestamp.map(ts => new Date(ts)));
        debug &&
            console.log('ABH Apportioned Slice BoI Dates: ', bucketsOfInterest.timestamp.map((ts) => new Date(ts)));
        // Range Checking - in case the desired range is older than the oldest bucket
        if (timestampRange[0] < bucketsOfInterest.timestamp[0]) {
            timestampRange[0] = bucketsOfInterest.timestamp[0];
        }
        // Add a bucket size because timestamps represent the start of a bucket, but the calculation we're about to do wants to cap at the end of a bucket
        if (timestampRange[1] > bucketsOfInterest.timestamp[bucketsOfInterest.getLastIndex()] + bucketSize) {
            timestampRange[1] = bucketsOfInterest.timestamp[bucketsOfInterest.getLastIndex()] + bucketSize;
        }
        // Reduce the first and last bucket based on amount of time
        /* Because bucket timestamps aren't contiguous, overlap can be calculated from a bucket far too long in the past, giving a negative result.
                However, this could only happen if there is no closest bucket in the desired region, which means the reading must be 0. */
        const startOverlap = bucketsOfInterest.timestamp[0] + bucketSize - timestampRange[0];
        const startPortion = startOverlap < 0 ? 0 : startOverlap / bucketSize;
        debug && console.log('ABH Start: ', startOverlap, startPortion);
        const lastBucketTimestamp = bucketsOfInterest.timestamp[bucketsOfInterest.getLastIndex()];
        const endOverlap = timestampRange[1] - lastBucketTimestamp;
        const lastBucketSize = latestTimestamp && latestTimestamp < lastBucketTimestamp + bucketSize ? latestTimestamp - lastBucketTimestamp : bucketSize;
        const endPortion = endOverlap / lastBucketSize;
        //const endOverlap = timestampRange[1] - bucketsOfInterest.timestamp[bucketsOfInterest.getLastIndex()]
        //const endPortion = endOverlap / bucketSize;
        debug && console.log('ABH End: ', endOverlap, endPortion);
        const lastIndex = bucketsOfInterest.getLastIndex();
        bucketsOfInterest.keys().forEach((key) => {
            bucketsOfInterest[key][0] *= startPortion;
            bucketsOfInterest[key][lastIndex] *= endPortion;
        });
        return bucketsOfInterest;
    }
    /* INFO: Explanation for latestTimestamp
     * A value for a reading in a bucket represents the value for the entire bucket
     * This goes a bit weird when the bucket intersects with the present, or is the latest value in the bucket history, because apportioning assumes
     * Imaging buckets are
     * -----------
     * | 30 | 10 |
     * -----------
     * 11   12
     * If we want to get 11:30 to 12:30, we take half of 30 and half of 10 to get 20 total.
     * But, if the 10 in the 12:00 bucket came in at 12:30, and it is currently 12:30, 10 actually represents only half of the bucket.
     * In reality, 11:30 to 12:30 is actually half of 30 + 10 = 45!
     * So instead of assuming the latest bucket represents the entire bucket period, you can specify the latest timestamp you know the bucketed history represents (usually the latest timestamp of the history store you are bucketing)
     * */
    total(key, timestampRange, latestTimestamp = undefined) {
        if (!this.hasKey(key))
            return undefined;
        const snaps = this.apportionedSliceByTimestamp(timestampRange, latestTimestamp).getStateSnapshotArray();
        debug && console.log(snaps);
        return snaps.reduce((total, snap) => {
            debug && console.log('Adding', snap.state[key]);
            return total + (snap.state[key] ?? 0);
        }, 0);
    }
}
exports.AutoBucketHistory = AutoBucketHistory;
AutoBucketHistory.create = ({ historyStore, defaultBucketStrategy, timeBucketDef, keyStrategyMap }) => {
    const bucketedHistory = new AutoBucketHistory({ timeBucketDef, defaultBucketStrategy, bucketStrategyMap: keyStrategyMap });
    const stateSnapshots = historyStore.getStateSnapshotArray();
    for (let stateSnapshot of stateSnapshots) {
        bucketedHistory.pushKeywise(stateSnapshot);
    }
    return bucketedHistory;
};
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number, String]),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "insertBucket", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "getLength", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "getLastTimestamp", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], AutoBucketHistory.prototype, "updateWithKeyAndBucket", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, String, Object]),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "addToBuckets", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "inferReading", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Boolean, Number]),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "push", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Array]),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "ingest", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Array]),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "ingestKeywise", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Object]),
    tslib_1.__metadata("design:returntype", void 0)
], AutoBucketHistory.prototype, "pushKeywise", null);
/** type check for hydrateable objects */
const isAutoBucketingHistoryObject = (object) => {
    const po = object;
    return !!(po.timeBucketDef && (0, iotv_v3_types_1.isHistoryObject)(po));
};
exports.isAutoBucketingHistoryObject = isAutoBucketingHistoryObject;
